import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { of, Observable, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ScoringForm } from '../models/forms/scoring.form';
import { League } from '../models/league';
import { LeagueStorage } from '../models/league.storage';
import { UUID } from '../models/uuid';

import { GenericService } from './generic.service';
import { PageableService } from './pageable.service';
import { GetPagedResponse, NameExistsResponse } from './responses/generic.responses';
import { GetScoringsResponse, GetScoringResponse } from './responses/scoring.responses';

@Injectable()
export class ScoringService extends GenericService implements PageableService {
  league: League;
  _propertySubscription: Subscription;
  constructor(
    private http: HttpClient,
    private leagueStorage: LeagueStorage,
    protected logger: NGXLogger
  ) {
    super(logger);
    this._propertySubscription = leagueStorage.getSelectedLeague().subscribe(league => {
      this.league = league;
    });
  }

  public checkNameTaken(name: string): Observable<boolean> {
    // if the current league is null (could happen during init), OR if this current scoring's name is the same as name, it's fine.
    if (this.league == null) {
      return of(false);
    }

    return this.http
      .post<NameExistsResponse>(
        `${environment.SCORING_API}/league/${this.league.id}/scoring/exists`,
        { name },
        this.httpOptions
      )
      .pipe(map(r => r.exists));
  }

  public getPagedData(params: any): Observable<GetPagedResponse> {
    return this.getScorings(params).pipe(
      map(r => {
        return {
          data: r.scorings,
          pagination: r.pagination
        };
      })
    );
  }

  public getScorings(params: any): Observable<GetScoringsResponse | any> {
    this.logger.debug('Called getScorings');
    if (this.league == null) {
      this.logger.debug('returning default scorings');
      return of(this.defaultResponse('scorings'));
    }
    this.logger.debug('calling get scorings service');

    const options = {
      headers: this.httpOptions.headers,
      params: new HttpParams({ fromObject: params })
    };

    return this.http
      .get<GetScoringsResponse>(
        `${environment.SCORING_API}/league/${this.league.id}/scoring`,
        options
      )
      .pipe(tap(response => this.logger.debug('getScorings response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getScoring(scoringId: UUID): Observable<GetScoringResponse | any> {
    this.logger.debug('Called getScoring');
    if (this.league == null) {
      this.logger.debug('returning default scoring');
      return of({ scoring: null });
    }

    this.logger.debug('calling get scoring service');
    return this.http
      .get<GetScoringResponse>(
        `${environment.SCORING_API}/league/${this.league.id}/scoring/${scoringId}`,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('getScoring response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public createScoring(value: ScoringForm): Observable<GetScoringResponse | any> {
    this.logger.debug('Called createScoring');
    return this.http
      .post<GetScoringResponse>(
        `${environment.SCORING_API}/league/${this.league.id}/scoring`,
        value,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('createScoring response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateScoring(value: ScoringForm): Observable<GetScoringResponse | any> {
    this.logger.debug('Called updateScoring');
    return this.http
      .patch<GetScoringResponse>(
        `${environment.SCORING_API}/league/${this.league.id}/scoring/${value.id}`,
        value,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('updateScoring response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public deleteScoring(scoringId: UUID): Observable<any | any> {
    this.logger.debug('Called deleteScoring');
    return this.http
      .delete<any>(
        `${environment.SCORING_API}/league/${this.league.id}/scoring/${scoringId}`,
        this.httpOptions
      )
      .pipe(tap(response => this.logger.debug('deleteScoring response:', response)))
      .pipe(catchError(this.handleError.bind(this)));
  }
}
